import type { ExtractedEvent } from '../types';
import { compareEventDates, eventDayKey, eventTodayKey, isAllDayEvent } from './eventDates';
import {
  addCalendarDays,
  addCalendarMonths,
  calendarWeekday,
  eventDateRangeUtc,
  eventDayStart,
  parseDateLabel,
  resolveEventLocalDateTime,
  type EventTemporalInput,
} from './eventTemporal';
import { eventTopicIds, matchesTimeOfDay, TIME_OF_DAY_VALUES } from './eventTopics';
import { tagGroupId } from './tagTaxonomy';

export type SortKey =
  | 'date-asc'
  | 'date-desc'
  | 'title-asc'
  | 'venue-asc'
  | 'recently-added'
  | 'distance-asc';

export interface Origin {
  /** Free-form label entered by the user (e.g. "1257 Glen Laura Road"). */
  label: string;
  lat: number;
  lng: number;
}

/**
 * Great-circle distance in miles between two lat/lng points (Haversine).
 * Returns Infinity if either point is missing — callers can then treat the
 * event as "unknown distance" and exclude it from radius filters.
 */
export function distanceMiles(
  aLat: number | undefined,
  aLng: number | undefined,
  bLat: number | undefined,
  bLng: number | undefined
): number {
  if (
    typeof aLat !== 'number' ||
    typeof aLng !== 'number' ||
    typeof bLat !== 'number' ||
    typeof bLng !== 'number'
  ) {
    return Infinity;
  }
  const R = 3958.7613; // Earth radius in miles
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export type DatePreset =
  | 'today'
  | 'weekend'
  | 'week'
  | 'month'
  | '60d'
  | 'all'
  | 'past-week'
  | 'past-month'
  | 'past-90d'
  | 'all-past'
  | 'all-time'
  | 'custom';

export interface FilterState {
  search: string;
  /** Broad topic ids from `EVENT_TOPICS` (any selected topic matches). */
  topics: Set<string>;
  /** Time-of-day buckets from `TIME_OF_DAY_OPTIONS` (any selected bucket matches). */
  times: Set<string>;
  /** Legacy venue-category filter. No longer offered in the bar; kept so old links and saved views still apply and can be cleared. */
  categories: Set<string>;
  neighborhoods: Set<string>;
  venues: Set<string>;
  sources: Set<string>;
  tags: Set<string>;
  /** Per-filter exclusion sets. An event with a matching value is dropped
   *  from the list regardless of any include sets. UI cycle ensures a value
   *  is in at most one of include/exclude at a time, but `applyFilters`
   *  treats exclude as the source of truth defensively. */
  topicsExcluded: Set<string>;
  timesExcluded: Set<string>;
  categoriesExcluded: Set<string>;
  neighborhoodsExcluded: Set<string>;
  venuesExcluded: Set<string>;
  sourcesExcluded: Set<string>;
  tagsExcluded: Set<string>;
  preset: DatePreset;
  /** New York calendar date (yyyy-MM-dd). Only used when `preset === 'custom'`. */
  customFrom?: string;
  /** New York calendar date (yyyy-MM-dd), inclusive end day. Only used when `preset === 'custom'`. */
  customTo?: string;
  hasImage: boolean;
  hasTicket: boolean;
  sort: SortKey;
  /** Origin point for distance filtering/sorting. Undefined = no distance filter. */
  origin?: Origin;
  /** Max distance in miles from `origin`. Undefined = no upper bound (still allows distance sort). */
  maxDistanceMiles?: number;
  /**
   * When true, reveal events whose tags overlap their venue's `excludedTags`
   * list (e.g. happy-hour / brunch promos a venue admin chose to hide).
   * Treated as a view setting, not a filter chip — preserved across `Clear`
   * and excluded from `countActiveFilters`.
   */
  showExcludedTags: boolean;
  /**
   * Tri-state recurring-event filter:
   *  - `'show-all'` (default): show every event including all projected weekly instances.
   *  - `'hide-instances'`: hide auto-projected non-anchor instances
   *    (`isRecurringInstance === true`). Anchors still appear.
   *  - `'only-repeats'`: show ONLY events that recur (anchors with a
   *    recurring pattern + their projected instances). Hides one-time events.
   *  - `'hide-weekly'`: hide every event with a recurring pattern (anchors
   *    AND projected instances). Only one-time events remain.
   */
  recurringMode: 'show-all' | 'hide-instances' | 'only-repeats' | 'hide-weekly';
  /**
   * Three-state venue-visibility filter mirrored on the public events list,
   * map, and calendar:
   *  - `'active-only'`: only events whose resolved venue is fully approved.
   *  - `'with-dismissed'` (default): active venues plus venues an admin
   *    dismissed (review-status `'dismissed'` or still `'pending'`) — i.e.
   *    "useful events even if the venue itself isn't surfaced".
   *  - `'all'`: also include events from declined / manually-deactivated
   *    venues. The events endpoint is called with `?includeHidden=1` so
   *    those rows are returned by the API at all.
   */
  venueVisibility: 'active-only' | 'with-dismissed' | 'all';
}

export const DEFAULT_FILTERS: FilterState = {
  search: '',
  topics: new Set(),
  times: new Set(),
  categories: new Set(),
  neighborhoods: new Set(),
  venues: new Set(),
  sources: new Set(),
  tags: new Set(),
  topicsExcluded: new Set(),
  timesExcluded: new Set(),
  categoriesExcluded: new Set(),
  neighborhoodsExcluded: new Set(),
  venuesExcluded: new Set(),
  sourcesExcluded: new Set(),
  tagsExcluded: new Set(),
  preset: '60d',
  customFrom: undefined,
  customTo: undefined,
  hasImage: false,
  hasTicket: false,
  sort: 'date-asc',
  origin: undefined,
  maxDistanceMiles: undefined,
  showExcludedTags: false,
  recurringMode: 'show-all',
  venueVisibility: 'with-dismissed',
};

export interface DateFilterWindow {
  from?: Date;
  /** Exclusive instant boundary, not 23:59:59 or midnight plus 24 hours. */
  to?: Date;
  fromDate?: string;
  /** Exclusive calendar boundary for all-day labels. */
  toDateExclusive?: string;
}

export interface EventDateWindow extends DateFilterWindow {
  from: Date;
  to: Date;
  fromDate: string;
  toDateExclusive: string;
}

export const DATE_WINDOW_ERROR =
  'Choose valid dates with the To date on or after the From date.';

/** Optional admin date bounds; supplied end dates are inclusive in the input. */
export function dateFilterWindow(fromDate?: string, toDateInclusive?: string): DateFilterWindow | null {
  if (fromDate && !parseDateLabel(fromDate)) return null;
  if (toDateInclusive && !parseDateLabel(toDateInclusive)) return null;
  if (fromDate && toDateInclusive && toDateInclusive < fromDate) return null;
  const toDateExclusive = toDateInclusive ? addCalendarDays(toDateInclusive, 1) : undefined;
  if (toDateInclusive && !toDateExclusive) return null;
  const from = fromDate ? eventDayStart(fromDate) : null;
  const to = toDateExclusive ? eventDayStart(toDateExclusive) : null;
  if ((from && !from.ok) || (to && !to.ok)) return null;
  return {
    from: from?.ok ? new Date(from.value.isoUtc) : undefined,
    to: to?.ok ? new Date(to.value.isoUtc) : undefined,
    fromDate: fromDate || undefined,
    toDateExclusive: toDateExclusive || undefined,
  };
}

/** Timed instants and source all-day labels obey the same half-open window. */
export function matchesDateWindow(event: EventTemporalInput, window: DateFilterWindow): boolean {
  if (!window.from && !window.to) return true;
  const key = eventDayKey(event);
  if (!key) return false;
  if (isAllDayEvent(event)) {
    return (!window.fromDate || key >= window.fromDate) &&
      (!window.toDateExclusive || key < window.toDateExclusive);
  }
  const ms = Date.parse(event.startUtc);
  return (!window.from || ms >= window.from.getTime()) && (!window.to || ms < window.to.getTime());
}

function calendarWindow(fromDate: string | null, toDateExclusive: string | null): EventDateWindow | null {
  if (!fromDate || !toDateExclusive) return null;
  const result = eventDateRangeUtc(fromDate, toDateExclusive);
  return result.ok ? {
    from: new Date(result.value.fromUtc), to: new Date(result.value.toUtc),
    fromDate, toDateExclusive,
  } : null;
}

/**
 * Resolve presets from New York calendar "now". All upper boundaries are
 * exclusive. Invalid/missing custom dates return null, never today's window.
 */
export function presetWindow(
  presetOrFilters: DatePreset | Pick<FilterState, 'preset' | 'customFrom' | 'customTo'>,
  now: Date = new Date(),
): EventDateWindow | null {
  const preset =
    typeof presetOrFilters === 'string' ? presetOrFilters : presetOrFilters.preset;
  const customFrom =
    typeof presetOrFilters === 'string' ? undefined : presetOrFilters.customFrom;
  const customTo =
    typeof presetOrFilters === 'string' ? undefined : presetOrFilters.customTo;
  if (!Number.isFinite(now.getTime())) return null;
  const start = eventTodayKey(now);
  switch (preset) {
    case 'today':
      return calendarWindow(start, addCalendarDays(start, 1));
    case 'weekend': {
      const day = calendarWeekday(start)!;
      const friday = addCalendarDays(start, day === 0 ? -2 : day === 6 ? -1 : 5 - day)!;
      const fromDate = day === 6 || day === 0 ? start : friday;
      const window = calendarWindow(fromDate, addCalendarDays(friday, 3));
      if (!window) return null;
      // Preserve the existing Friday-after-5pm weekend rule for timed events;
      // all-day Friday activities retain their source calendar date.
      if (day !== 6 && day !== 0) {
        const from = resolveEventLocalDateTime(friday, '17:00');
        if (!from.ok) return null;
        window.from = new Date(from.value.isoUtc);
      }
      return window;
    }
    case 'week':
      return calendarWindow(start, addCalendarDays(start, 7));
    case 'month':
      return calendarWindow(start, addCalendarMonths(start, 1));
    case '60d':
      return calendarWindow(start, addCalendarDays(start, 60));
    case 'all':
      return calendarWindow(start, addCalendarMonths(start, 12));
    case 'past-week':
      return calendarWindow(addCalendarDays(start, -7), addCalendarDays(start, 1));
    case 'past-month':
      return calendarWindow(addCalendarMonths(start, -1), addCalendarDays(start, 1));
    case 'past-90d':
      return calendarWindow(addCalendarDays(start, -90), addCalendarDays(start, 1));
    case 'all-past':
      return calendarWindow(addCalendarMonths(start, -60), addCalendarDays(start, 1));
    case 'all-time':
      return calendarWindow(addCalendarMonths(start, -60), addCalendarMonths(start, 12));
    case 'custom': {
      if (!customFrom || !customTo || !parseDateLabel(customTo)) return null;
      return calendarWindow(customFrom, addCalendarDays(customTo, 1));
    }
  }
  return null;
}

export function uniqSorted(values: (string | undefined)[]): string[] {
  return Array.from(new Set(values.filter((v): v is string => !!v && v.trim().length > 0))).sort((a, b) =>
    a.localeCompare(b)
  );
}

export function toggle(set: Set<string>, value: string): Set<string> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

/**
 * Time-of-day used to be a tag group (`tags=evening`). Move those values into
 * the clock-based `times` filter so old links and saved views keep working.
 */
export function migrateLegacyTimeTags(f: FilterState): FilterState {
  const move = (tags: Set<string>, times: Set<string>) => {
    const nextTags = new Set<string>();
    const nextTimes = new Set(times);
    for (const t of tags) {
      if (TIME_OF_DAY_VALUES.has(t)) nextTimes.add(t);
      else nextTags.add(t);
    }
    return [nextTags, nextTimes] as const;
  };
  const [tags, times] = move(f.tags, f.times);
  const [tagsExcluded, timesExcluded] = move(f.tagsExcluded, f.timesExcluded);
  return { ...f, tags, times, tagsExcluded, timesExcluded };
}

export function readFiltersFromUrl(params: URLSearchParams): FilterState {
  const split = (key: string) =>
    new Set((params.get(key) ?? '').split(',').map((s) => s.trim()).filter(Boolean));
  const oLat = parseFloat(params.get('oLat') ?? '');
  const oLng = parseFloat(params.get('oLng') ?? '');
  const oLbl = params.get('oLbl') ?? '';
  const origin: Origin | undefined =
    Number.isFinite(oLat) && Number.isFinite(oLng)
      ? { label: oLbl, lat: oLat, lng: oLng }
      : undefined;
  const distRaw = parseFloat(params.get('dist') ?? '');
  const maxDistanceMiles =
    Number.isFinite(distRaw) && distRaw > 0 ? distRaw : undefined;
  return migrateLegacyTimeTags({
    search: params.get('q') ?? '',
    topics: split('topic'),
    times: split('tod'),
    categories: split('cat'),
    neighborhoods: split('hood'),
    venues: split('venue'),
    sources: split('src'),
    tags: split('tags'),
    topicsExcluded: split('topic_x'),
    timesExcluded: split('tod_x'),
    categoriesExcluded: split('cat_x'),
    neighborhoodsExcluded: split('hood_x'),
    venuesExcluded: split('venue_x'),
    sourcesExcluded: split('src_x'),
    tagsExcluded: split('tags_x'),
    preset: (params.get('when') as DatePreset) ?? '60d',
    customFrom: params.get('from') ?? undefined,
    customTo: params.get('to') ?? undefined,
    hasImage: params.get('img') === '1',
    hasTicket: params.get('tix') === '1',
    sort: (params.get('sort') as SortKey) ?? 'date-asc',
    origin,
    maxDistanceMiles,
    showExcludedTags: params.get('xtag') === '1',
    recurringMode:
      params.get('rec') === 'hide'
        ? 'hide-instances'
        : params.get('rec') === 'noweekly'
        ? 'hide-weekly'
        : params.get('rec') === 'only'
        ? 'only-repeats'
        : 'show-all',
    venueVisibility:
      params.get('vv') === 'active'
        ? 'active-only'
        : params.get('vv') === 'all'
          ? 'all'
          : 'with-dismissed',
  });
}

export function writeFiltersToUrl(
  f: FilterState,
  extra: Record<string, string | undefined>,
  setParams: (p: URLSearchParams) => void
): void {
  const p = new URLSearchParams();
  if (f.search) p.set('q', f.search);
  if (f.topics.size) p.set('topic', [...f.topics].join(','));
  if (f.times.size) p.set('tod', [...f.times].join(','));
  if (f.categories.size) p.set('cat', [...f.categories].join(','));
  if (f.neighborhoods.size) p.set('hood', [...f.neighborhoods].join(','));
  if (f.venues.size) p.set('venue', [...f.venues].join(','));
  if (f.sources.size) p.set('src', [...f.sources].join(','));
  if (f.tags.size) p.set('tags', [...f.tags].join(','));
  if (f.topicsExcluded.size) p.set('topic_x', [...f.topicsExcluded].join(','));
  if (f.timesExcluded.size) p.set('tod_x', [...f.timesExcluded].join(','));
  if (f.categoriesExcluded.size) p.set('cat_x', [...f.categoriesExcluded].join(','));
  if (f.neighborhoodsExcluded.size) p.set('hood_x', [...f.neighborhoodsExcluded].join(','));
  if (f.venuesExcluded.size) p.set('venue_x', [...f.venuesExcluded].join(','));
  if (f.sourcesExcluded.size) p.set('src_x', [...f.sourcesExcluded].join(','));
  if (f.tagsExcluded.size) p.set('tags_x', [...f.tagsExcluded].join(','));
  if (f.preset !== '60d') p.set('when', f.preset);
  if (f.preset === 'custom') {
    if (f.customFrom) p.set('from', f.customFrom);
    if (f.customTo) p.set('to', f.customTo);
  }
  if (f.hasImage) p.set('img', '1');
  if (f.hasTicket) p.set('tix', '1');
  if (f.sort !== 'date-asc') p.set('sort', f.sort);
  if (f.origin) {
    p.set('oLat', f.origin.lat.toFixed(6));
    p.set('oLng', f.origin.lng.toFixed(6));
    if (f.origin.label) p.set('oLbl', f.origin.label);
  }
  if (typeof f.maxDistanceMiles === 'number' && f.maxDistanceMiles > 0) {
    p.set('dist', String(f.maxDistanceMiles));
  }
  if (f.showExcludedTags) p.set('xtag', '1');
  if (f.recurringMode === 'hide-instances') p.set('rec', 'hide');
  else if (f.recurringMode === 'hide-weekly') p.set('rec', 'noweekly');
  else if (f.recurringMode === 'only-repeats') p.set('rec', 'only');
  if (f.venueVisibility === 'active-only') p.set('vv', 'active');
  else if (f.venueVisibility === 'all') p.set('vv', 'all');
  for (const [k, v] of Object.entries(extra)) {
    if (v) p.set(k, v);
  }
  setParams(p);
}

/** Apply filters + sort to an event list. Date-window filtering is handled
 *  server-side via the `from`/`to` query params, so we do not re-filter by date here. */
export function applyFilters(events: ExtractedEvent[], filters: FilterState): ExtractedEvent[] {
  const q = filters.search.trim().toLowerCase();
  // Selected tags in the same group (e.g. two genres) are alternatives; tags
  // from different groups (genre + audience) must all match. Tags outside the
  // taxonomy each form their own group, as before.
  const requiredTagGroups = new Map<string, string[]>();
  for (const t of filters.tags) {
    const key = tagGroupId(t) ?? `tag:${t}`;
    requiredTagGroups.set(key, [...(requiredTagGroups.get(key) ?? []), t]);
  }
  const origin = filters.origin;
  const maxDist = filters.maxDistanceMiles;
  // Memoise per-event distance so the filter pass and the sort pass share work.
  const distCache = origin ? new Map<string, number>() : null;
  const distOf = (e: ExtractedEvent): number => {
    if (!origin) return Infinity;
    const cached = distCache!.get(e.rowKey);
    if (cached !== undefined) return cached;
    const d = distanceMiles(origin.lat, origin.lng, e.venueLat, e.venueLng);
    distCache!.set(e.rowKey, d);
    return d;
  };
  let list = events.filter((e) => {
    // Hide events whose tags overlap the venue's admin-defined excluded
    // list, unless the user toggled them on. Keeps things like happy-hour
    // and brunch promos out of the default view for venues that opted in.
    if (!filters.showExcludedTags && e.venueExcludedTags?.length && e.tags?.length) {
      const excluded = e.venueExcludedTags;
      if (e.tags.some((t) => excluded.includes(t))) return false;
    }
    if (filters.topics.size || filters.topicsExcluded.size) {
      const topics = eventTopicIds(e);
      if (filters.topics.size && ![...filters.topics].some((t) => topics.has(t))) return false;
      if ([...filters.topicsExcluded].some((t) => topics.has(t))) return false;
    }
    if (filters.times.size && ![...filters.times].some((t) => matchesTimeOfDay(e, t))) return false;
    if (filters.timesExcluded.size && [...filters.timesExcluded].some((t) => matchesTimeOfDay(e, t))) return false;
    if (filters.categories.size && !(e.category && filters.categories.has(e.category))) return false;
    if (filters.neighborhoods.size && !(e.neighborhood && filters.neighborhoods.has(e.neighborhood))) return false;
    if (filters.venues.size && !(e.venueId && filters.venues.has(e.venueId))) return false;
    if (filters.sources.size && !filters.sources.has(e.sourceType)) return false;
    for (const group of requiredTagGroups.values()) {
      if (!e.tags?.some((t) => group.includes(t))) return false;
    }
    // Per-filter exclusions: drop the event if any of its values appear in the
    // matching exclude set. Defensive — UI cycle keeps include/exclude
    // mutually exclusive, but exclude wins if both ever land set.
    if (filters.categoriesExcluded.size && e.category && filters.categoriesExcluded.has(e.category)) return false;
    if (filters.neighborhoodsExcluded.size && e.neighborhood && filters.neighborhoodsExcluded.has(e.neighborhood)) return false;
    if (filters.venuesExcluded.size && e.venueId && filters.venuesExcluded.has(e.venueId)) return false;
    if (filters.sourcesExcluded.size && filters.sourcesExcluded.has(e.sourceType)) return false;
    if (filters.tagsExcluded.size && e.tags?.some((t) => filters.tagsExcluded.has(t))) return false;
    if (filters.hasImage && !e.imageUrl) return false;
    if (filters.hasTicket && !e.ticketUrl) return false;
    // Recurring-event filter. Anchor = `isRecurringInstance` is false/undefined.
    // Default `'show-all'` lets everything through.
    if (filters.recurringMode === 'hide-instances' && e.isRecurringInstance === true) return false;
    if (filters.recurringMode === 'hide-weekly') {
      if (e.isRecurringInstance === true) return false;
      if (e.recurringPattern && e.recurringPattern !== 'one-time') return false;
    }
    if (filters.recurringMode === 'only-repeats') {
      const isInstance = e.isRecurringInstance === true;
      const isRecurringAnchor = !!e.recurringPattern && e.recurringPattern !== 'one-time';
      if (!isInstance && !isRecurringAnchor) return false;
    }
    // Venue-visibility filter. Treat events with no `venueVisibility` field
    // as 'active' so legacy / cached payloads behave as before.
    if (filters.venueVisibility !== 'all') {
      const vv = e.venueVisibility ?? 'active';
      if (filters.venueVisibility === 'active-only' && vv !== 'active') return false;
      // 'with-dismissed' allows 'active' and 'dismissed' but drops 'hidden'.
      // (The API only returns 'hidden' when includeHidden=1 was sent.)
      if (filters.venueVisibility === 'with-dismissed' && vv === 'hidden') return false;
    }
    if (q) {
      const hay = `${e.title} ${e.description ?? ''} ${e.venueName ?? ''} ${e.locationText ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    // Distance filter is opt-in: only applied when the user has supplied
    // both an origin AND a max distance. Events with no venue coordinates
    // are excluded (they cannot satisfy the radius constraint).
    if (origin && typeof maxDist === 'number' && maxDist > 0) {
      if (distOf(e) > maxDist) return false;
    }
    return true;
  });
  list = [...list];
  // Within a single calendar day, all-day events float to the top so they
  // never end up wedged between timed entries (matches the in-day ordering
  // used by CalendarView and the date-grouped EventList renderer).
  switch (filters.sort) {
    case 'date-desc':
      list.sort((a, b) => compareEventDates(a, b, -1));
      break;
    case 'title-asc':
      list.sort((a, b) => a.title.localeCompare(b.title));
      break;
    case 'venue-asc':
      list.sort((a, b) => (a.venueName ?? '').localeCompare(b.venueName ?? ''));
      break;
    case 'recently-added':
      list.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
      break;
    case 'distance-asc':
      // Events without coordinates sink to the bottom (Infinity > all).
      list.sort((a, b) => distOf(a) - distOf(b));
      break;
    default:
      list.sort(compareEventDates);
  }
  return list;
}

export function countActiveFilters(f: FilterState): number {
  return (
    f.topics.size +
    f.times.size +
    f.topicsExcluded.size +
    f.timesExcluded.size +
    f.categories.size +
    f.neighborhoods.size +
    f.venues.size +
    f.sources.size +
    f.tags.size +
    f.categoriesExcluded.size +
    f.neighborhoodsExcluded.size +
    f.venuesExcluded.size +
    f.sourcesExcluded.size +
    f.tagsExcluded.size +
    (f.search ? 1 : 0) +
    (f.hasImage ? 1 : 0) +
    (f.hasTicket ? 1 : 0) +
    (f.origin && typeof f.maxDistanceMiles === 'number' && f.maxDistanceMiles > 0 ? 1 : 0) +
    (f.recurringMode !== 'show-all' ? 1 : 0) +
    (f.venueVisibility !== 'with-dismissed' ? 1 : 0)
  );
}

/**
 * Cycle a single value through three states for a paired include/exclude pair:
 * `off → include → exclude → off`. Returns the next pair of sets. Caller should
 * spread the result into the FilterState update.
 */
export function cycleIncludeExclude(
  active: ReadonlySet<string>,
  excluded: ReadonlySet<string>,
  value: string,
): { active: Set<string>; excluded: Set<string> } {
  const nextActive = new Set(active);
  const nextExcluded = new Set(excluded);
  if (nextExcluded.has(value)) {
    // exclude → off
    nextExcluded.delete(value);
  } else if (nextActive.has(value)) {
    // include → exclude
    nextActive.delete(value);
    nextExcluded.add(value);
  } else {
    // off → include
    nextActive.add(value);
  }
  return { active: nextActive, excluded: nextExcluded };
}
